import React, { useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  AccessTime,
  Schedule,
  EventBusy,
  CheckCircle,
  Cancel,
  TrendingUp,
  Assessment,
  Coffee,
  Restaurant,
  Wc,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { apiClient, timeEntriesAPI, timeAbsenceRequestAPI } from '../services/api';
import { TimezoneService } from '../services/timezoneService';
import moment from 'moment-timezone';
import ActivityProductivityCard from '../components/ActivityProductivityCard';
import { mergeScheduleHours } from '../utils/scheduleIntervalUtils';

const AgentHomePage: React.FC = () => {
  const { user } = useAuth();
  const { trackingEnabled } = useSettings();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for live clock
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const userTimezone = user?.timezone || TimezoneService.getUserTimezone();

  // Get today's schedule
  const { data: todaySchedulesData, isLoading: scheduleLoading } = useQuery({
    queryKey: ['todaySchedules'],
    queryFn: async () => {
      const response = await apiClient.schedules.getMyTodaySchedule();
      return response; // Return full axios response structure
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Get current clock status
  const { data: clockStatusData, isLoading: clockStatusLoading } = useQuery({
    queryKey: ['clockStatus'],
    queryFn: () => timeEntriesAPI.getStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Get recent time off requests
  const { data: timeOffRequests, isLoading: requestsLoading } = useQuery({
    queryKey: ['myTimeOffRequests'],
    queryFn: () => timeAbsenceRequestAPI.getMyRequests(),
  });

  // Get today's time entries to calculate hours worked
  const { data: todayEntriesData, isLoading: summaryLoading } = useQuery({
    queryKey: ['todayEntries'],
    queryFn: async () => {
      const today = moment().tz(userTimezone).format('YYYY-MM-DD');
      const response = await apiClient.timeEntries.getReport({
        startDate: today,
        endDate: today,
        userId: user?.id
      });
      return response.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const isLoading = scheduleLoading || clockStatusLoading || requestsLoading || summaryLoading;

  // Check if user's device is excluded from tracking
  const { data: myDevicesData } = useQuery({
    queryKey: ['myDevices'],
    queryFn: () => apiClient.devices.getAll({ userId: user?.id, isActive: true }),
    enabled: !!user?.id,
  });

  const myDevices = myDevicesData?.data?.data || [];
  const hasExcludedDevice = myDevices.some((d: any) => !d.isTracked);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      </Container>
    );
  }

  const clockStatus = clockStatusData?.data?.data || { status: 'clocked_out' };
  const isClockedIn = clockStatus.status === 'clocked_in';
  const isOnBreak = clockStatus.status === 'on_break';
  const isOnLunch = clockStatus.status === 'on_lunch';
  const isOnBathroom = clockStatus.status === 'on_bathroom';
  const isActivelyOnShift = isClockedIn || isOnBreak || isOnLunch || isOnBathroom;
  const currentCampaign = clockStatus.currentCampaign;
  const lastEntry = clockStatus.lastEntry;

  // Get status-specific colors and text (matching Clock In/Out screen colors but lighter)
  const getStatusConfig = () => {
    switch (clockStatus.status) {
      case 'clocked_in':
        // Success green - lighter version
        return {
          gradient: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
          title: 'You are Clocked In',
          icon: <CheckCircle sx={{ fontSize: 60, opacity: 0.9 }} />
        };
      case 'on_break':
        // Warning orange - lighter, softer version
        return {
          gradient: 'linear-gradient(135deg, #ffcc80 0%, #ffb74d 100%)',
          title: 'You are on Break',
          icon: <Coffee sx={{ fontSize: 60, opacity: 0.9 }} />
        };
      case 'on_bathroom':
        // Secondary purple — distinct from Break (orange) and Lunch (blue)
        return {
          gradient: 'linear-gradient(135deg, #ce93d8 0%, #ab47bc 100%)',
          title: 'You are on Bathroom',
          icon: <Wc sx={{ fontSize: 60, opacity: 0.9 }} />
        };
      case 'on_lunch':
        // Info blue - lighter version
        return {
          gradient: 'linear-gradient(135deg, #64b5f6 0%, #42a5f5 100%)',
          title: 'You are on Lunch',
          icon: <Restaurant sx={{ fontSize: 60, opacity: 0.9 }} />
        };
      case 'clocked_out':
      default:
        // Red mixed with pink - softer version (matches Clock Out button)
        return {
          gradient: 'linear-gradient(135deg, #f48fb1 0%, #ef5350 100%)',
          title: 'You are Clocked Out',
          icon: <Cancel sx={{ fontSize: 60, opacity: 0.9 }} />
        };
    }
  };

  const statusConfig = getStatusConfig();

  // Get schedule before using it  
  // todaySchedulesData structure: { data: { success: boolean; data: Schedule[] } }
  const scheduleData = todaySchedulesData?.data?.data;
  const schedules = Array.isArray(scheduleData) ? scheduleData : [];

  // Calculate hours worked today from time entries
  let hoursWorked = 0;
  const entries = todayEntriesData?.data || [];
  if (Array.isArray(entries) && entries.length > 0) {
    let sessionStart: Date | null = null;
    let totalMinutes = 0;

    for (const entry of entries) {
      if (entry.entryType === 'clock_in' && !sessionStart) {
        sessionStart = new Date(entry.timestamp);
      } else if (entry.entryType === 'clock_out' && sessionStart) {
        const sessionEnd = new Date(entry.timestamp);
        const minutes = (sessionEnd.getTime() - sessionStart.getTime()) / (1000 * 60);
        totalMinutes += minutes;
        sessionStart = null;
      }
    }

    // If still clocked in, calculate up to now
    if (sessionStart && isClockedIn) {
      const now = new Date();
      const minutes = (now.getTime() - sessionStart.getTime()) / (1000 * 60);
      totalMinutes += minutes;
    }

    hoursWorked = totalMinutes / 60;
  }

  const scheduledHours = schedules.length > 0
    ? mergeScheduleHours(schedules, userTimezone)
    : 0;

  // Get pending time off requests count
  const pendingRequests = timeOffRequests?.data?.data?.filter(
    (req: any) => req.status === 'pending' || req.status === 'in_progress'
  ) || [];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome, {user?.firstName}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {currentTime.toLocaleTimeString('en-US', {
            hour12: true,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })} • {moment.tz(currentTime, userTimezone).format('dddd, MMMM D, YYYY')}
        </Typography>
      </Box>

      {/* Clock Status Card - Primary Focus */}
      <Card sx={{ 
        mb: 3, 
        background: statusConfig.gradient,
        boxShadow: 3
      }}>
        <CardContent sx={{ color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {statusConfig.title}
              </Typography>
              {(isActivelyOnShift) && currentCampaign && (
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Campaign: {currentCampaign.name}
                </Typography>
              )}
              {lastEntry && (
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                  {isActivelyOnShift
                    ? (isOnBreak
                        ? 'Break started at'
                        : isOnBathroom
                          ? 'Bathroom started at'
                          : isOnLunch
                            ? 'Lunch started at'
                            : 'Clocked in at')
                    : 'Last clock out'} {TimezoneService.formatDateTimeConsistent(
                    typeof lastEntry === 'string' ? lastEntry : (lastEntry.timestamp || ''),
                    userTimezone
                  )}
                </Typography>
              )}
            </Box>
            <Box>
              {statusConfig.icon}
            </Box>
          </Box>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => navigate('/clock')}
            sx={{
              mt: 2,
              bgcolor: 'white',
              color: 'primary.main',
              borderColor: 'white',
              borderWidth: 2,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                borderColor: 'white',
                borderWidth: 2,
              },
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 'bold',
            }}
            startIcon={<AccessTime />}
          >
            Go to Clock In/Out
          </Button>
        </CardContent>
      </Card>

      {/* Alert Banner: Tracking Status */}
      {trackingEnabled && hasExcludedDevice && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Device Excluded from Tracking:</strong> One or more of your devices are excluded from activity tracking. Contact your administrator if you believe this is an error.
          </Typography>
        </Alert>
      )}

      {/* Today's Information Grid */}
      <Grid container spacing={3}>
        {/* Today's Schedule */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Today's Schedule</Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/my-schedule')}
                  startIcon={<Schedule />}
                >
                  View All
                </Button>
              </Box>
              {schedules.length > 0 ? (
                <Stack spacing={1.5}>
                  {schedules.map((schedule: any) => (
                    <Box key={schedule.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          label={schedule.campaign?.name || 'No Campaign'}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={schedule.isActive ? 'Active' : 'Inactive'}
                          color={schedule.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Time:</strong> {schedule.startTimeLocal && schedule.endTimeLocal ? (
                          `${schedule.startTimeLocal} - ${schedule.endTimeLocal}`
                        ) : schedule.startTime && schedule.endTime ? (
                          `${schedule.startTime} - ${schedule.endTime}`
                        ) : (
                          'Time not available'
                        )}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        <strong>Date:</strong> {TimezoneService.formatDateForDisplay(
                          schedule.date,
                          userTimezone
                        )}
                      </Typography>
                    </Box>
                  ))}
                  {schedules.length > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      Combined scheduled time today: {scheduledHours}h (overlapping shifts counted once)
                    </Typography>
                  )}
                </Stack>
              ) : (
                <Alert severity="info">
                  No schedule for today
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Today's Summary
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {hoursWorked.toFixed(1)}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hours Worked
                  </Typography>
                </Box>
                {scheduledHours > 0 && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h4" color="text.secondary" fontWeight="bold">
                        {scheduledHours}h
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Scheduled
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
              {scheduledHours > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp
                    fontSize="small"
                    color={hoursWorked >= scheduledHours ? 'success' : 'warning'}
                  />
                  <Typography
                    variant="body2"
                    color={hoursWorked >= scheduledHours ? 'success.main' : 'warning.main'}
                  >
                    {hoursWorked >= scheduledHours
                      ? 'On track with schedule'
                      : `${(scheduledHours - hoursWorked).toFixed(1)}h remaining`}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Time Off Requests */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Time Off Requests</Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/time-absence-requests')}
                  startIcon={<EventBusy />}
                >
                  View All
                </Button>
              </Box>
              {pendingRequests.length > 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You have {pendingRequests.length} pending request{pendingRequests.length !== 1 ? 's' : ''}
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No pending requests
                </Typography>
              )}
              {timeOffRequests?.data?.data?.slice(0, 3).map((req: any) => (
                <Box key={req.id} sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {req.type.charAt(0).toUpperCase() + req.type.slice(1)}
                    </Typography>
                    <Chip
                      label={req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                      size="small"
                      color={
                        req.status === 'approved' ? 'success' :
                        req.status === 'rejected' ? 'error' :
                        'warning'
                      }
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {moment(req.startDate).format('MMM D')} - {moment(req.endDate).format('MMM D')}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Activity & Productivity */}
        <Grid item xs={12} md={6}>
          <ActivityProductivityCard />
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AccessTime />}
                    onClick={() => navigate('/clock')}
                  >
                    Clock In/Out
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Schedule />}
                    onClick={() => navigate('/my-schedule')}
                  >
                    My Schedule
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<EventBusy />}
                    onClick={() => navigate('/time-absence-requests')}
                  >
                    Time Off
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Assessment />}
                    onClick={() => navigate('/reports')}
                  >
                    My Reports
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AgentHomePage;

